// components/PhotoViewer.jsx - View and manage captured photos
import React, { useState } from 'react';

const PhotoViewer = ({ photos, onDelete, onAnnotate, onReorder }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  if (photos.length === 0) {
    return null;
  }

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    
    // Reorder photos
    const newPhotos = [...photos];
    const [removed] = newPhotos.splice(dragIndex, 1);
    newPhotos.splice(index, 0, removed);
    
    onReorder(newPhotos);
    setDragIndex(index);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDragIndex(null);
  };

  return (
    <div>
      {/* Thumbnail grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
        gap: '8px',
        marginTop: '12px'
      }}>
        {photos.map((photo, index) => (
          <div
            key={photo.timestamp || index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            onClick={() => setSelectedIndex(index)}
            style={{
              position: 'relative',
              aspectRatio: '1',
              borderRadius: '8px',
              overflow: 'hidden',
              cursor: 'pointer',
              border: dragIndex === index ? '2px solid #2563eb' : '2px solid transparent',
              opacity: dragIndex === index ? 0.5 : 1
            }}
          >
            <img
              src={photo.previewUrl}
              alt={`Photo ${index + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            
            {/* Photo number badge */}
            <div style={{
              position: 'absolute',
              top: '4px',
              left: '4px',
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {index + 1}
            </div>

            {/* Annotated indicator */}
            {photo.annotated && (
              <div style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                backgroundColor: '#2563eb',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '10px'
              }}>
                ✏️
              </div>
            )}

            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(index);
              }}
              style={{
                position: 'absolute',
                bottom: '4px',
                right: '4px',
                width: '24px',
                height: '24px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Full-screen preview modal */}
      {selectedIndex !== null && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: 'white', fontWeight: '500' }}>
              Photo {selectedIndex + 1} of {photos.length}
            </span>
            <button
              onClick={() => setSelectedIndex(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px'
              }}
            >
              ✕
            </button>
          </div>

          {/* Image */}
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}>
            <img
              src={photos[selectedIndex].previewUrl}
              alt={`Photo ${selectedIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
            />
          </div>

          {/* Actions */}
          <div style={{
            padding: '16px',
            display: 'flex',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <button
              onClick={() => {
                onAnnotate(selectedIndex);
                setSelectedIndex(null);
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ✏️ Annotate
            </button>
            <button
              onClick={() => {
                onDelete(selectedIndex);
                setSelectedIndex(null);
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              🗑️ Delete
            </button>
          </div>

          {/* Navigation arrows */}
          {photos.length > 1 && (
            <>
              <button
                onClick={() => setSelectedIndex(prev => (prev - 1 + photos.length) % photos.length)}
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '24px'
                }}
              >
                ‹
              </button>
              <button
                onClick={() => setSelectedIndex(prev => (prev + 1) % photos.length)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '24px'
                }}
              >
                ›
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoViewer;